import React, { useRef, useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import { Formik } from 'formik';

import logEvent from '@zr/utilities/logEvent';
import LabeledField from '@zr/components/Fields/LabeledField';
import apiRequest from '@zr/utilities/api';

import { RESET_PASSWORD_URL, PW_CHECK_REGEX, TOO_COMMON_ERROR, TOO_SHORT_ERROR,
  MINIMUM_PASSWORD_LENGTH } from './constants';

import { checkPwComplexity, resetPwCache } from './checkPwComplexity';

import './SetPasswordForm.less';

// Renders a form that prompts the user to set their password
export default function SetPasswordForm({ onSubmit, onError, setIsLoading }) {
  // used to display pwComplexityCheck feedback to user
  const [note, setNote] = useState(null);

  // for debouncing password complexity check requests
  const timeout = useRef(null);

  // returns a promise that resolves true for valid password, false otherwise
  const checkPw = async (password) => {
    clearTimeout(timeout.current);

    if (password) { // undefined if field is empty
      return new Promise((resolve, reject) => {
        timeout.current = setTimeout(async () => {
          try {
            const { message, type } = await checkPwComplexity(password);

            setNote({ message, type });
            resolve(type !== 'error');
          } catch (err) {
            reject(err);
          }
        }, 500);
      });
    }
    return false; // empty field
  };

  const handleSave = async (values, { setStatus }) => {
    // set the new password
    const params = new URLSearchParams({
      password: values.password,
      set_password_method: 'fe.post_apply.set_password',
    });

    // `setIsLoading(true)` so that we can disable the close button while the
    // screen is processing/loading
    setIsLoading(true);

    const fetchInit = {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
      },
      body: params.toString(),
    };

    try {
      const response = await apiRequest(RESET_PASSWORD_URL, fetchInit);
      const json = await response.json();

      if (!json.success) {
        onError();
      } else {
        // successfully set password. set form status so we can disable the input
        // fields while we wait for the parent component to handle the submit
        setStatus({ success: true });
        resetPwCache();
        onSubmit();
      }
    } catch {
      onError();
    }
  };

  useEffect(() => {
    logEvent('post_apply_password_view');
  }, []);

  // runs on password field change and blur
  const validatePasswordField =  async (value) => {
    let passwordError = '';

    clearTimeout(timeout.current); // cancel any in progress async validation

    if (!value) {
      passwordError = 'Please enter a password.';
      setNote(null); // clear any old password complexity feedback
    } else if (value.length < MINIMUM_PASSWORD_LENGTH) {
      passwordError = TOO_SHORT_ERROR;
      setNote(null);
    } else if (PW_CHECK_REGEX.test(value)) {
      passwordError = TOO_COMMON_ERROR;
      setNote(null);
    } else {
      // passed client side checks, send to api
      try {
        const valid = await checkPw(value);

        if (!valid) {
          passwordError = TOO_COMMON_ERROR;
        }
      } catch {
        // if api call for check fails assume the input is invalid since it wasn't checked
        passwordError = 'Unable to validate password.';
        onError();
      }
    }

    return passwordError;
  };

  // runs on password field confirm change and blur
  const validateConfirmField = (value, passwordValue) => {
    let passwordConfirmError = '';

    if (!value) {
      passwordConfirmError = 'Please confirm your new password.';
    } else if (value.length < MINIMUM_PASSWORD_LENGTH) {
      passwordConfirmError = TOO_SHORT_ERROR;
    } else if (value !== passwordValue) {
      passwordConfirmError = 'Please enter the same password as above.';
    }

    return passwordConfirmError;
  };

  // runs on submit, will block submission while the async check runs
  const validateAllFields = async (values) => {
    const errors = {};

    const passwordError = await validatePasswordField(values.password);
    const passwordConfirmError = validateConfirmField(values.passwordConfirm, values.password);

    if (passwordError) { errors.password = passwordError; }
    if (passwordConfirmError) { errors.passwordConfirm = passwordConfirmError; }

    return errors;
  };

  return (
    <Formik
      onSubmit={handleSave}
      initialValues={{
        password: '',
        passwordConfirm: '',
      }}
      validateOnChange={false}
      validateOnBlur={false}
      validate={validateAllFields}
    >
      {({
        handleSubmit, setFieldTouched, setFieldError, setFieldValue, touched, values, errors, isSubmitting, status,
      }) => {
        // handle field validation by hand to have better control over timing of async validation
        // for the password field. see https://github.com/formium/formik/issues/512#issuecomment-643788203
        const handlePasswordBlur = async (e) => {
          setFieldTouched('password', true);

          setFieldError('password', ''); // clear error while we wait for potentially async call
          const passwordError = await validatePasswordField(e.target.value);

          setFieldError('password', passwordError);
        };

        const handlePasswordChange = async (e) => {
          setFieldValue('password', e.target.value);

          setFieldError('password', ''); // clear error while we wait for potentially async call
          const passwordError = await validatePasswordField(e.target.value);

          setFieldError('password', passwordError);
        };

        const handlePasswordConfirmBlur = (e) => {
          setFieldTouched('passwordConfirm', true);
          setFieldError('passwordConfirm', validateConfirmField(e.target.value, values.password));
        };

        const handlePasswordConfirmChange = (e) => {
          setFieldValue('passwordConfirm', e.target.value);
          setFieldError('passwordConfirm', validateConfirmField(e.target.value, values.password));
        };

        return (
          <section className="SetPasswordForm">
            <img className="image" src="/img/candidate/set_password.svg" width="230" height="178" alt="security shield" />
            <h1 className="headline">Keep your account secure. Set a password.</h1>
            <p className="subheadline">
              Setting a password ensures you can access your account in the future from any device.
              <span className="hint">{` Minimum of ${MINIMUM_PASSWORD_LENGTH} characters.`}</span>
            </p>
            <form className="set_password_form" onSubmit={handleSubmit}>
              <fieldset>
                <legend className="set_password_legend">Password</legend>
                <LabeledField
                  id="password"
                  label="Password"
                  placeholder="Password"
                  type="password"
                  onChange={handlePasswordChange}
                  onBlur={handlePasswordBlur}
                  input={{
                    name: 'password',
                    value: values.password,
                    disabled: isSubmitting || status?.success,
                  }}
                  meta={{
                    error: errors.password,
                    touched: touched.password,
                  }}
                />
                <LabeledField
                  id="passwordConfirm"
                  label="Confirm password"
                  placeholder="Confirm password"
                  type="password"
                  onChange={handlePasswordConfirmChange}
                  onBlur={handlePasswordConfirmBlur}
                  input={{
                    name: 'passwordConfirm',
                    value: values.passwordConfirm,
                    disabled: isSubmitting || status?.success,
                  }}
                  meta={{
                    error: errors.passwordConfirm,
                    touched: touched.passwordConfirm,
                  }}
                />
              </fieldset>
              <p className={`password_note ${note?.type}`} aria-live="polite">
                {note?.message}
              </p>
              <button className="cta" type="submit" disabled={isSubmitting || status?.success}>Set Password</button>
            </form>
          </section>
        );
      }}
    </Formik>
  );
}

SetPasswordForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onError: PropTypes.func,
  setIsLoading: PropTypes.func,
};

SetPasswordForm.defaultProps = {
  onError: () => {},
  setIsLoading: () => {},
};
